import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Determines if a background color is dark, useful for choosing appropriate text color
 * @param hexColor - Hex color string (with or without #)
 * @returns true if dark background (should use white text), false if light (should use black text)
 */
export function isBackgroundDark(hexColor: string): boolean {
  // Remove # if present
  const hex = hexColor.replace('#', '');
  
  // Handle both 3-digit and 6-digit hex colors
  let r, g, b;
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  } else if (hex.length === 6) {
    r = parseInt(hex.substr(0, 2), 16);
    g = parseInt(hex.substr(2, 2), 16);
    b = parseInt(hex.substr(4, 2), 16);
  } else {
    // Invalid hex color, default to light (black text)
    return false;
  }
  
  // Calculate luminance using standard formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b);
  
  // Return true if dark (luminance < 128), false if light
  return luminance < 128;
}

/**
 * Gets the appropriate text color (black or white) based on background color
 * @param backgroundColor - Hex background color
 * @returns "#ffffff" for dark backgrounds, "#000000" for light backgrounds
 */
export function getTextColorForBackground(backgroundColor: string): string {
  return isBackgroundDark(backgroundColor) ? "#ffffff" : "#000000";
}

/**
 * Height utility constants and functions for consistent layout calculations
 */
export const NAVBAR_HEIGHT = '3rem'; // h-12 (48px) + 1px border = 49px total

/**
 * Gets the CSS class for full viewport height minus navbar
 * @returns Tailwind class string for min-height calculation
 */
export function getContentMinHeight(): string {
  return `min-h-[calc(100vh-${NAVBAR_HEIGHT})]`;
}

/**
 * Gets the CSS class for full screen height minus navbar  
 * @returns Tailwind class string for min-height calculation using screen height
 */
export function getContentMinScreenHeight(): string {
  return `min-h-[calc(100svh-${NAVBAR_HEIGHT})]`;
}
